import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faTrash as faTrashSolid } from '@fortawesome/pro-solid-svg-icons';
import { IIndustryResponse } from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useState } from 'react';
import { tokens } from 'styles/tokens';
import { Filter } from 'utils/query_v3';

export type DeleteIndustryModalProps = Readonly<{
    industry: IIndustryResponse;
    onDelete: () => void;
}>;

const validateDelete = async (id: number) => {
    const data = await sdClient.getCorporations({ filter: Filter.eq('IndustryId', id) });
    if (data.Result.length >= 1) {
        return 'Industry is in use. Please remove all corporations that use this industry before deleting.';
    }
    return undefined;
};

export function DeleteIndustryModal({
    industry: { Id, Name },
    onDelete,
}: DeleteIndustryModalProps) {
    const { showToast } = useNotificationContext();
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const { mutate: deleteIndustry, isPending: isDeleting } = useMutation({
        mutationFn: () => sdClient.deleteIndustry({ id: Id }),

        onSuccess: () => {
            setConfirmationModalOpen(false);
            onDelete();
            showToast({
                type: 'success',
                content: 'Successfully deleted industry',
            });
        },

        onError: async () => {
            setConfirmationModalOpen(false);
            const validationError = await validateDelete(Id);
            if (validationError) {
                showToast({
                    type: 'error',
                    content: validationError,
                });
            } else {
                showToast({
                    type: 'error',
                    content: 'Failed to delete industry',
                });
            }
        },
    });

    return (
        <>
            <HoverIconButton
                icon={faTrash}
                hoverIcon={faTrashSolid}
                color={tokens.text.secondary}
                hoverColor={tokens.error.default}
                title="Delete Industry"
                onClick={() => setConfirmationModalOpen(true)}
                disabled={isDeleting}
            />
            {confirmationModalOpen && (
                <ConfirmModal
                    title={`Delete '${Name}'`}
                    actionText="delete this industry? The industry will no longer be available."
                    checkText="delete this industry"
                    actionButtonText="Delete"
                    onClose={() => setConfirmationModalOpen(false)}
                    onAction={deleteIndustry}
                    closable={!isDeleting}
                    actionable={!isDeleting}
                />
            )}
        </>
    );
}
